import React, { useMemo } from 'react';
import { Pagination, Empty } from 'antd';
import { ActivityLog, LogFilters } from './ActivityLog.types';
import {
  DEPARTMENT_CONFIG,
  USER_TYPE_CONFIG,
  STATUS_CONFIG,
  formatDateTime,
  applyFilters,
  getIcon,
} from './ActivityLogSampleData';

const PAGE_SIZE = 10;

interface Props {
  logs: ActivityLog[];
  total: number;
  loading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  onSelect: (log: ActivityLog) => void;
}

const LogCard: React.FC<{ log: ActivityLog; onClick: () => void }> = ({ log, onClick }) => {
  const deptCfg = DEPARTMENT_CONFIG[log.department];
  const userCfg = USER_TYPE_CONFIG[log.userType];
  const statusCfg = STATUS_CONFIG[log.status];
  const { date, time } = formatDateTime(log.timestamp);

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        padding: '14px 18px',
        background: '#fff',
        border: '1px solid #f0f0f0',
        borderLeft: `4px solid ${deptCfg.color}`,
        borderRadius: 10,
        cursor: 'pointer',
        transition: 'all 0.15s',
        marginBottom: 8,
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.background = '#f7f8ff';
        el.style.borderColor = '#d0d8ff';
        el.style.boxShadow = '0 3px 12px rgba(0,0,0,0.06)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.background = '#fff';
        el.style.borderColor = '#f0f0f0';
        el.style.boxShadow = 'none';
      }}
    >
      {/* Department icon bubble */}
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: 9,
          background: deptCfg.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {getIcon(deptCfg.iconKey, { fontSize: 20, color: deptCfg.color })}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Row 1: status dot + action + status badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: statusCfg.color,
              flexShrink: 0,
              display: 'inline-block',
            }}
          />
          <span style={{ fontWeight: 600, fontSize: 14, color: '#1a1a2e' }}>{log.action}</span>
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 11,
              fontWeight: 500,
              background: statusCfg.bg,
              color: statusCfg.color,
              borderRadius: 5,
              padding: '2px 8px',
              flexShrink: 0,
            }}
          >
            {statusCfg.label}
          </span>
        </div>

        {/* Row 2: description */}
        <div
          style={{
            fontSize: 13,
            color: '#555',
            lineHeight: 1.55,
            marginBottom: 8,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {log.description}
        </div>

        {/* Row 3: chips + datetime */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11,
              fontWeight: 500,
              background: deptCfg.bg,
              color: deptCfg.color,
              borderRadius: 6,
              padding: '3px 9px',
            }}
          >
            {getIcon(deptCfg.iconKey, { fontSize: 12 })}
            {deptCfg.label}
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11,
              fontWeight: 500,
              background: userCfg.bg,
              color: userCfg.color,
              borderRadius: 6,
              padding: '3px 9px',
            }}
          >
            {getIcon(userCfg.iconKey, { fontSize: 12 })}
            {userCfg.label}
          </span>
          <span style={{ fontSize: 12, color: '#888' }}>{log.userName}</span>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#aaa', flexShrink: 0 }}>
            {date} · {time}
          </span>
        </div>
      </div>
    </div>
  );
};

const ActivityLogCards: React.FC<Props> = ({ logs, total, loading, page, pageSize, onPageChange, onSelect }) => {
  const paginated = logs;

  if (paginated.length === 0) {
    return <Empty description="No activity logs match your filters" style={{ margin: '48px 0' }} />;
  }

  return (
    <>
      {paginated.map((log) => (
        <LogCard key={log.id} log={log} onClick={() => onSelect(log)} />
      ))}
      {logs.length > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <Pagination
            current={page}
            total={logs.length}
            pageSize={PAGE_SIZE}
            onChange={onPageChange}
            showSizeChanger={false}
            showTotal={(total, range) => `${range[0]}–${range[1]} of ${total}`}
          />
        </div>
      )}
    </>
  );
};

export default ActivityLogCards;
